// src/cockpit4/components/CustomerSuccess.tsx
// ============================================
// P1-功能6：客户成功管理（客户中心）
//
// 功能：
//   1. 自动启动 onboarding 流程（成交客户自动进入 onboarding，分阶段：交付→培训→上线→稳定）
//   2. 定期回访和满意度调查（30/60/90 天回访提醒）
//   3. 使用情况监控和健康度评分（基于活跃度/反馈/续费意愿计算健康度）
//   4. 增购/复购机会识别（高健康度客户标记增购机会）
//   5. 客户成功 SOP 执行（内置 SOP 清单，勾选执行）
//
// 数据来源：
//   - RuntimeManager.query(RuntimeName.CRM, { type: "list" }) 筛选已成交客户
//   - localStorage（key: xeaos_customer_success）持久化 onboarding/健康度/SOP 进度
//   - 无数据时显示空状态提示，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HeartHandshake,
  RefreshCw,
  Loader2,
  Users,
  Activity,
  TrendingUp,
  CheckCircle2,
  Circle,
  CalendarClock,
  AlertTriangle,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Gift,
  GraduationCap,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { CustomerProfile } from "../../runtime/CRMRuntime/types";

// ============================================
// 类型与常量
// ============================================

const CS_STORAGE_KEY = "xeaos_customer_success";

/** onboarding 阶段 */
type OnboardingStage = "delivery" | "training" | "launch" | "stable";

/** 客户成功记录（按 customerId 索引） */
interface SuccessRecord {
  customerId: string;
  customerName: string;
  /** onboarding 阶段 */
  onboardingStage: OnboardingStage;
  /** onboarding 各阶段 SOP 勾选状态 */
  sopChecklist: Record<string, boolean>;
  /** 健康度评分 0-100 */
  healthScore: number;
  /** 健康度维度明细 */
  healthBreakdown: { activity: number; feedback: number; renewal: number };
  /** 满意度调查记录 */
  surveys: SurveyRecord[];
  /** 标记的增购机会 */
  upsellOpportunities: string[];
  /** 最后更新日期 */
  updatedAt: string;
}

/** 满意度调查记录 */
interface SurveyRecord {
  id: string;
  date: string;
  /** 第几次回访：30/60/90 */
  milestone: 30 | 60 | 90;
  score: number; // 1-10
  feedback: string;
}

/** onboarding 阶段元数据 */
interface OnboardingStageMeta {
  id: OnboardingStage;
  label: string;
  color: string;
  icon: typeof GraduationCap;
  /** SOP 清单 */
  sop: string[];
}

/** 内置 onboarding SOP（非客户数据，是流程模板） */
const ONBOARDING_STAGES: OnboardingStageMeta[] = [
  {
    id: "delivery",
    label: "交付",
    color: "#60A5FA",
    icon: Gift,
    sop: [
      "组建交付团队，明确项目成员与职责",
      "召开项目启动会，对齐目标与时间表",
      "完成产品/服务交付物清单确认",
      "建立客户对接群与沟通机制",
    ],
  },
  {
    id: "training",
    label: "培训",
    color: "#A78BFA",
    icon: GraduationCap,
    sop: [
      "制定培训计划（管理员/终端用户）",
      "完成管理员后台操作培训",
      "完成终端用户使用培训",
      "提供培训文档与视频回放",
    ],
  },
  {
    id: "launch",
    label: "上线",
    color: "#FBBF24",
    icon: TrendingUp,
    sop: [
      "完成数据初始化与系统配置",
      "执行试运行并收集问题",
      "正式上线切换",
      "上线首周驻场/远程支持",
    ],
  },
  {
    id: "stable",
    label: "稳定",
    color: "#34D399",
    icon: CheckCircle2,
    sop: [
      "建立定期回访机制（月度/季度）",
      "输出客户使用报告与价值复盘",
      "识别增购/复购机会",
      "启动续费提醒流程",
    ],
  },
];

/** 回访里程碑 */
const SURVEY_MILESTONES: { day: 30 | 60 | 90; label: string; color: string }[] = [
  { day: 30, label: "30 天回访", color: "#60A5FA" },
  { day: 60, label: "60 天回访", color: "#A78BFA" },
  { day: 90, label: "90 天回访", color: "#34D399" },
];

// ============================================
// 工具函数
// ============================================

const DAY_MS = 24 * 60 * 60 * 1000;

function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/** 计算距今天数（无效日期返回 -1） */
function daysSince(dateStr: string): number {
  if (!dateStr) return -1;
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return -1;
  return Math.floor((Date.now() - t) / DAY_MS);
}

/** 安全读取 localStorage */
function loadRecords(): Record<string, SuccessRecord> {
  try {
    const raw = localStorage.getItem(CS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, SuccessRecord>) : {};
  } catch {
    return {};
  }
}

/** 安全写入 localStorage */
function saveRecords(records: Record<string, SuccessRecord>): void {
  try {
    localStorage.setItem(CS_STORAGE_KEY, JSON.stringify(records));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/**
 * 计算客户健康度（0-100）
 * 维度：
 *   - activity（活跃度）：基于 lastContact 距今天数，30天内满分
 *   - feedback（反馈）：基于 history 条目数与满意度均值
 *   - renewal（续费意愿）：基于 tier 与 estimatedValue
 */
function calcHealth(customer: CustomerProfile, record: SuccessRecord | undefined): {
  score: number;
  breakdown: { activity: number; feedback: number; renewal: number };
} {
  // 活跃度（0-40）
  const d = daysSince(customer.followup?.lastContact || "");
  let activity = 20;
  if (d >= 0) {
    if (d <= 7) activity = 40;
    else if (d <= 14) activity = 32;
    else if (d <= 30) activity = 24;
    else if (d <= 60) activity = 12;
    else activity = 4;
  }

  // 反馈（0-30）：满意度均值 + history 条目数
  let feedback = 10;
  const historyCount = customer.history
    ? (customer.history.match(/-\s*\d{4}-\d{2}-\d{2}\s*:/g) || []).length
    : 0;
  feedback += Math.min(10, historyCount * 2);
  if (record && record.surveys.length > 0) {
    const avgScore = record.surveys.reduce((s, x) => s + x.score, 0) / record.surveys.length;
    feedback += Math.round((avgScore / 10) * 10); // 满意度 0-10 映射 0-10
  }

  // 续费意愿（0-30）：tier 基础分 + 高金额加成（封顶 30）
  const tierScore: Record<string, number> = { "S+": 30, S: 26, A: 20, B: 12, C: 6 };
  let renewal = tierScore[customer.tier] || 10;
  const value = customer.followup?.estimatedValue || 0;
  if (value >= 100000) renewal = Math.min(30, renewal + 4);

  const score = Math.max(0, Math.min(100, activity + feedback + renewal));
  return { score, breakdown: { activity, feedback, renewal } };
}

/** 健康度分级 */
function healthLevel(score: number): { label: string; color: string } {
  if (score >= 80) return { label: "健康", color: "#34D399" };
  if (score >= 60) return { label: "良好", color: "#FBBF24" };
  if (score >= 40) return { label: "预警", color: "#FB923C" };
  return { label: "风险", color: "#F87171" };
}

/** 判断是否需要某里程碑回访 */
function needsSurvey(customer: CustomerProfile, milestone: 30 | 60 | 90, record: SuccessRecord | undefined): boolean {
  // 已成交客户，距上次联系天数 >= milestone - 30（提前 30 天提醒），且尚未完成该里程碑调查
  const d = daysSince(customer.followup?.lastContact || "");
  if (d < 0) return false;
  if (d < milestone - 30) return false;
  if (record && record.surveys.some((s) => s.milestone === milestone)) return false;
  return true;
}

// ============================================
// 主组件
// ============================================

export default function CustomerSuccess() {
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [records, setRecords] = useState<Record<string, SuccessRecord>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // 调查录入草稿（按 customerId_milestone）
  const [surveyDrafts, setSurveyDrafts] = useState<Record<string, { score: string; feedback: string }>>({});

  // 加载真实客户列表
  const loadCustomers = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setError("RuntimeManager 尚未初始化");
        setLoading(false);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.CRM, { type: "list" });
      if (resp?.success && resp.data) {
        const inner = resp.data as { data?: CustomerProfile[] } | CustomerProfile[];
        const list = Array.isArray(inner) ? inner : inner?.data || [];
        setCustomers(Array.isArray(list) ? list : []);
      } else {
        setError(resp?.error || "查询客户列表失败");
      }
    } catch (err) {
      setError((err as Error).message || "加载客户列表异常");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // 初次挂载
  useEffect(() => {
    setRecords(loadRecords());
    void loadCustomers();
  }, [loadCustomers]);

  // 记录变化时持久化
  useEffect(() => {
    saveRecords(records);
  }, [records]);

  // 筛选已成交客户（closed-won / active）
  const closedCustomers = useMemo(
    () => customers.filter((c) => c.followup?.status === "closed-won" || c.followup?.status === "active"),
    [customers]
  );

  // 自动为已成交但无记录的客户初始化 onboarding
  useEffect(() => {
    if (closedCustomers.length === 0) return;
    setRecords((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const c of closedCustomers) {
        if (!next[c.id]) {
          // 初始化：交付阶段 + 空 SOP
          const sopChecklist: Record<string, boolean> = {};
          for (const stage of ONBOARDING_STAGES) {
            for (const item of stage.sop) {
              sopChecklist[`${stage.id}:${item}`] = false;
            }
          }
          const initRecord: SuccessRecord = {
            customerId: c.id,
            customerName: c.company,
            onboardingStage: "delivery",
            sopChecklist,
            healthScore: 0,
            healthBreakdown: { activity: 0, feedback: 0, renewal: 0 },
            surveys: [],
            upsellOpportunities: [],
            updatedAt: todayStr(),
          };
          const { score, breakdown } = calcHealth(c, initRecord);
          initRecord.healthScore = score;
          initRecord.healthBreakdown = breakdown;
          next[c.id] = initRecord;
          changed = true;
        } else {
          // 重新计算健康度（客户数据可能更新）
          const existing = next[c.id];
          const { score, breakdown } = calcHealth(c, existing);
          if (existing.healthScore !== score || existing.healthBreakdown.activity !== breakdown.activity) {
            next[c.id] = {
              ...existing,
              healthScore: score,
              healthBreakdown: breakdown,
              updatedAt: todayStr(),
            };
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [closedCustomers]);

  /** 切换 SOP 勾选状态 */
  const toggleSop = useCallback((customerId: string, sopKey: string) => {
    setRecords((prev) => {
      const r = prev[customerId];
      if (!r) return prev;
      return {
        ...prev,
        [customerId]: {
          ...r,
          sopChecklist: { ...r.sopChecklist, [sopKey]: !r.sopChecklist[sopKey] },
          updatedAt: todayStr(),
        },
      };
    });
  }, []);

  /** 推进 onboarding 阶段 */
  const advanceStage = useCallback((customerId: string, stage: OnboardingStage) => {
    setRecords((prev) => {
      const r = prev[customerId];
      if (!r) return prev;
      return {
        ...prev,
        [customerId]: { ...r, onboardingStage: stage, updatedAt: todayStr() },
      };
    });
  }, []);

  /** 提交满意度调查 */
  const submitSurvey = useCallback(
    (customerId: string, milestone: 30 | 60 | 90) => {
      const draftKey = `${customerId}_${milestone}`;
      const draft = surveyDrafts[draftKey];
      if (!draft) return;
      const score = Number(draft.score);
      if (!score || score < 1 || score > 10) return;
      setRecords((prev) => {
        const r = prev[customerId];
        if (!r) return prev;
        // 去重：同一里程碑只保留最新
        const surveys = r.surveys.filter((s) => s.milestone !== milestone);
        surveys.push({
          id: genId("survey"),
          date: todayStr(),
          milestone,
          score,
          feedback: draft.feedback.trim(),
        });
        return { ...prev, [customerId]: { ...r, surveys, updatedAt: todayStr() } };
      });
      setSurveyDrafts((s) => {
        const next = { ...s };
        delete next[draftKey];
        return next;
      });
    },
    [surveyDrafts]
  );

  /** 标记/取消增购机会 */
  const toggleUpsell = useCallback((customerId: string, opportunity: string) => {
    setRecords((prev) => {
      const r = prev[customerId];
      if (!r) return prev;
      const exists = r.upsellOpportunities.includes(opportunity);
      const upsellOpportunities = exists
        ? r.upsellOpportunities.filter((x) => x !== opportunity)
        : [...r.upsellOpportunities, opportunity];
      return { ...prev, [customerId]: { ...r, upsellOpportunities, updatedAt: todayStr() } };
    });
  }, []);

  // 概览统计
  const overview = useMemo(() => {
    const total = closedCustomers.length;
    const inOnboarding = Object.values(records).filter((r) => r.onboardingStage !== "stable").length;
    const stable = Object.values(records).filter((r) => r.onboardingStage === "stable").length;
    const avgHealth =
      Object.values(records).length > 0
        ? Math.round(Object.values(records).reduce((s, r) => s + r.healthScore, 0) / Object.values(records).length)
        : 0;
    const upsellCount = Object.values(records).reduce((s, r) => s + r.upsellOpportunities.length, 0);
    return { total, inOnboarding, stable, avgHealth, upsellCount };
  }, [closedCustomers, records]);

  // 需要回访提醒的客户数
  const surveyReminders = useMemo(() => {
    let count = 0;
    for (const c of closedCustomers) {
      const r = records[c.id];
      for (const m of SURVEY_MILESTONES) {
        if (needsSurvey(c, m.day, r)) {
          count++;
          break;
        }
      }
    }
    return count;
  }, [closedCustomers, records]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-emerald-500/15 border border-emerald-500/30">
            <HeartHandshake size={13} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">客户成功管理</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Customer Success · {overview.total} 成交客户 · 均健康度 {overview.avgHealth} · 待回访 {surveyReminders}
            </p>
          </div>
        </div>
        <button
          onClick={loadCustomers}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
        >
          {refreshing ? (
            <Loader2 size={11} className="text-emerald-400 animate-spin" />
          ) : (
            <RefreshCw size={11} className="text-emerald-400" />
          )}
          <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">
            {refreshing ? "刷新中..." : "刷新"}
          </span>
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className="px-3 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06] text-red-300 text-[10px] font-mono">
            {error}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-emerald-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载成交客户...</p>
        </div>
      ) : closedCustomers.length === 0 ? (
        <div className="py-10 text-center">
          <Users size={24} className="mx-auto text-gray-600 mb-2" />
          <p className="text-[11px] font-mono text-gray-500">暂无已成交客户</p>
          <p className="text-[9px] font-mono text-gray-700 mt-1">
            筛选条件：followup.status ∈ {`{ closed-won, active }`}
          </p>
        </div>
      ) : (
        <>
          {/* 顶部 KPI */}
          <div className="grid grid-cols-4 gap-2 p-4 border-b border-white/[0.04]">
            <KpiCard icon={Users} label="成交客户" value={String(overview.total)} color="#34D399" />
            <KpiCard icon={Activity} label="Onboarding 中" value={String(overview.inOnboarding)} color="#FBBF24" />
            <KpiCard icon={HeartHandshake} label="平均健康度" value={`${overview.avgHealth}`} color="#38BDF8" />
            <KpiCard icon={Sparkles} label="增购机会" value={String(overview.upsellCount)} color="#A78BFA" />
          </div>

          {/* 回访提醒横幅 */}
          {surveyReminders > 0 && (
            <div className="px-4 py-2 border-b border-white/[0.04]">
              <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/[0.06] text-amber-300 text-[10px] font-mono">
                <AlertTriangle size={11} />
                <span>有 {surveyReminders} 位客户需要回访调查（30/60/90 天里程碑）</span>
              </div>
            </div>
          )}

          {/* 客户成功列表 */}
          <div className="p-4 space-y-1.5">
            {closedCustomers.map((c) => {
              const record = records[c.id];
              if (!record) return null;
              const health = healthLevel(record.healthScore);
              const isExpanded = expandedId === c.id;
              const stageMeta = ONBOARDING_STAGES.find((s) => s.id === record.onboardingStage)!;
              const StageIcon = stageMeta.icon;
              // 当前阶段 SOP 进度
              const currentSopItems = stageMeta.sop.map((item) => ({
                key: `${stageMeta.id}:${item}`,
                label: item,
                done: !!record.sopChecklist[`${stageMeta.id}:${item}`],
              }));
              const doneCount = currentSopItems.filter((x) => x.done).length;
              const stageIdx = ONBOARDING_STAGES.findIndex((s) => s.id === record.onboardingStage);

              return (
                <div
                  key={c.id}
                  className="rounded-md border border-white/[0.04] bg-white/[0.02] overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : c.id)}
                    className="w-full flex items-center gap-2.5 p-2.5 text-left hover:bg-white/[0.03] transition-colors"
                  >
                    {/* 健康度 */}
                    <div className="flex-shrink-0 w-12 text-center">
                      <div className="text-[16px] font-mono font-bold" style={{ color: health.color }}>
                        {record.healthScore}
                      </div>
                      <div className="text-[8px] font-mono uppercase" style={{ color: health.color }}>
                        {health.label}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[12px] text-gray-200 truncate">{c.company}</span>
                        {record.upsellOpportunities.length > 0 && (
                          <Sparkles size={11} className="text-violet-400 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span
                          className="flex items-center gap-0.5 px-1 rounded text-[8px] font-mono"
                          style={{ background: `${stageMeta.color}20`, color: stageMeta.color }}
                        >
                          <StageIcon size={9} />
                          {stageMeta.label}（{doneCount}/{currentSopItems.length}）
                        </span>
                        <span className="text-[9px] font-mono text-gray-500">{c.tier}</span>
                        <span className="text-[9px] font-mono text-gray-600">·</span>
                        <span className="text-[9px] font-mono text-gray-500">{c.industry || "—"}</span>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown size={11} className="text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronRight size={11} className="text-gray-500 flex-shrink-0" />
                    )}
                  </button>

                  {/* 展开内容：onboarding 阶段条 + SOP + 回访 + 增购 */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-white/[0.04] overflow-hidden"
                      >
                        <div className="p-2.5 space-y-2.5">
                          {/* onboarding 阶段进度条 */}
                          <div>
                            <div className="text-[9px] font-mono text-gray-500 uppercase mb-1.5">
                              Onboarding 流程
                            </div>
                            <div className="flex items-center gap-1">
                              {ONBOARDING_STAGES.map((s, idx) => {
                                const isDone = idx < stageIdx;
                                const isCurrent = idx === stageIdx;
                                const Icon = s.icon;
                                return (
                                  <button
                                    key={s.id}
                                    onClick={() => advanceStage(c.id, s.id)}
                                    className="flex-1 flex items-center justify-center gap-1 px-1 py-1.5 rounded border text-[9px] font-mono transition-colors"
                                    style={{
                                      background: isCurrent ? `${s.color}20` : isDone ? `${s.color}10` : "transparent",
                                      borderColor: isCurrent ? `${s.color}60` : isDone ? `${s.color}30` : "rgba(255,255,255,0.04)",
                                      color: isCurrent || isDone ? s.color : "#6B7280",
                                    }}
                                    title={`点击切换到「${s.label}」阶段`}
                                  >
                                    <Icon size={10} />
                                    {s.label}
                                    {isDone && <CheckCircle2 size={9} />}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* 当前阶段 SOP 清单 */}
                          <div>
                            <div className="text-[9px] font-mono text-gray-500 uppercase mb-1.5">
                              {stageMeta.label}阶段 SOP（{doneCount}/{currentSopItems.length}）
                            </div>
                            <div className="space-y-1">
                              {currentSopItems.map((item) => (
                                <button
                                  key={item.key}
                                  onClick={() => toggleSop(c.id, item.key)}
                                  className="w-full flex items-center gap-2 p-1.5 rounded bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04] transition-colors text-left"
                                >
                                  {item.done ? (
                                    <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0" />
                                  ) : (
                                    <Circle size={11} className="text-gray-600 flex-shrink-0" />
                                  )}
                                  <span
                                    className={`text-[10px] font-mono ${
                                      item.done ? "text-gray-500 line-through" : "text-gray-300"
                                    }`}
                                  >
                                    {item.label}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* 健康度明细 */}
                          <div>
                            <div className="text-[9px] font-mono text-gray-500 uppercase mb-1.5">健康度明细</div>
                            <div className="space-y-1">
                              {[
                                { key: "activity", label: "活跃度", value: record.healthBreakdown.activity, max: 40, color: "#60A5FA" },
                                { key: "feedback", label: "反馈", value: record.healthBreakdown.feedback, max: 30, color: "#A78BFA" },
                                { key: "renewal", label: "续费意愿", value: record.healthBreakdown.renewal, max: 30, color: "#34D399" },
                              ].map((dim) => (
                                <div key={dim.key} className="flex items-center gap-2">
                                  <span className="w-16 text-[10px] font-mono text-gray-400">{dim.label}</span>
                                  <div className="flex-1 h-2 rounded bg-white/[0.04] overflow-hidden">
                                    <div
                                      className="h-full rounded"
                                      style={{
                                        width: `${(dim.value / dim.max) * 100}%`,
                                        background: dim.color,
                                      }}
                                    />
                                  </div>
                                  <span className="text-[9px] font-mono text-gray-400 w-10 text-right">
                                    {dim.value}/{dim.max}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* 回访里程碑 */}
                          <div>
                            <div className="text-[9px] font-mono text-gray-500 uppercase mb-1.5">
                              回访与满意度调查
                            </div>
                            <div className="space-y-1.5">
                              {SURVEY_MILESTONES.map((m) => {
                                const survey = record.surveys.find((s) => s.milestone === m.day);
                                const needs = needsSurvey(c, m.day, record);
                                const draftKey = `${c.id}_${m.day}`;
                                const draft = surveyDrafts[draftKey] || { score: "", feedback: "" };
                                return (
                                  <div
                                    key={m.day}
                                    className="rounded bg-white/[0.02] border border-white/[0.04] p-1.5"
                                  >
                                    <div className="flex items-center gap-2 mb-1">
                                      <CalendarClock size={10} style={{ color: m.color }} />
                                      <span
                                        className="text-[10px] font-mono"
                                        style={{ color: m.color }}
                                      >
                                        {m.label}
                                      </span>
                                      {survey ? (
                                        <span className="text-[9px] font-mono text-emerald-400">
                                          已完成 · 评分 {survey.score}/10
                                        </span>
                                      ) : needs ? (
                                        <span className="text-[9px] font-mono text-amber-400">待回访</span>
                                      ) : (
                                        <span className="text-[9px] font-mono text-gray-600">未到时间</span>
                                      )}
                                    </div>
                                    {survey ? (
                                      <div className="text-[9px] font-mono text-gray-500">
                                        {survey.feedback || "（无反馈文字）"}
                                      </div>
                                    ) : (
                                      <div className="grid grid-cols-3 gap-1.5">
                                        <input
                                          value={draft.score}
                                          onChange={(e) =>
                                            setSurveyDrafts((s) => ({
                                              ...s,
                                              [draftKey]: { ...draft, score: e.target.value },
                                            }))
                                          }
                                          placeholder="评分 1-10"
                                          className="px-1.5 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
                                        />
                                        <input
                                          value={draft.feedback}
                                          onChange={(e) =>
                                            setSurveyDrafts((s) => ({
                                              ...s,
                                              [draftKey]: { ...draft, feedback: e.target.value },
                                            }))
                                          }
                                          placeholder="客户反馈"
                                          className="col-span-2 px-1.5 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
                                        />
                                        <button
                                          onClick={() => submitSurvey(c.id, m.day)}
                                          disabled={!draft.score || Number(draft.score) < 1 || Number(draft.score) > 10}
                                          className="col-span-3 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                          提交调查
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* 增购/复购机会 */}
                          <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <Sparkles size={10} className="text-violet-400" />
                              <span className="text-[9px] font-mono text-gray-500 uppercase">
                                增购/复购机会（健康度 ≥ 80 时建议标记）
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {["增购模块", "扩容席位", "续费升级", "交叉销售", "推荐新客户"].map((opp) => {
                                const marked = record.upsellOpportunities.includes(opp);
                                return (
                                  <button
                                    key={opp}
                                    onClick={() => toggleUpsell(c.id, opp)}
                                    className={`px-1.5 py-0.5 rounded text-[9px] font-mono border transition-colors ${
                                      marked
                                        ? "bg-violet-500/15 border-violet-500/40 text-violet-300"
                                        : "bg-white/[0.02] border-white/[0.06] text-gray-500 hover:bg-white/[0.05]"
                                    }`}
                                  >
                                    {marked ? "✓ " : ""}
                                    {opp}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </>
      )}
    </motion.div>
  );
}

// ============================================
// KPI 小卡片
// ============================================
interface KpiCardProps {
  icon: typeof Users;
  label: string;
  value: string;
  color: string;
}

function KpiCard({ icon: Icon, label, value, color }: KpiCardProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={13} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">{label}</div>
        <div className="text-[13px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}
